import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../assets/close.svg';
import { useTranslation } from 'react-i18next';
import { GoEyeClosed, GoEye } from 'react-icons/go';
import SpinnerIcon from '../assets/spinner2.svg';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import log from '../utils/logger';
import api from '../api/api';
import useToast from '../hooks/useToast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import Toast from './Toast';
import ForgotPasswordModal from './ForgotPasswordModal';


const LoginModalHome = ({ isOpen, onClose, email }) => {
  useBodyScrollLock(isOpen);
  const { t, i18n } = useTranslation();
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useUser();
  const { setCart, setCartId } = useCart();
  const { toastMessage, toastType, showToast } = useToast();
  const navigate = useNavigate();
  const passwordInputRef = useRef(null);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setShow(true);
        passwordInputRef.current?.focus();
      }, 10);
    } else {
      setShow(false);
    }
  }, [isOpen]);

  const handlePasswordToggle = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (email) {setUserEmail(email);}
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/user/login', {
        userEmail,
        password,
      });

      const { id_token, user, userCart, cartId } = response.data.response?.data ?? {};

      login(user, id_token);

      setCart(userCart || []);
      setCartId(cartId || null);

      const userLang = user.language;
      const defaultLang = 'en';

      if (typeof userLang === 'string' && userLang.length === 2) {
        i18n.changeLanguage(userLang).catch(err => {
          log.warn('Failed to change language:', err);
        });
      } else {
        i18n.changeLanguage(defaultLang);
      }

      navigate('/home', { replace: true });

      window.dispatchEvent(new CustomEvent('user-logged-in'));

      onClose();

    } catch (error) {
      if (error.response) {
        // Server responded with a status outside the 2xx range
        const status = error.response.status;
        const message = error.response.data?.message || 'Login failed';

        log.error(message);

        if (status === 401) {
          showToast(t('loginerror1'), 'error');
        } else if (status === 403) {
          showToast(t('loginerror2'), 'error');
        } else {
          showToast(t('loginerror3'), 'error');
        }
      } else {
        log.error(error);
        showToast(t('loginerror4'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <>
      <div>
        {/* Overlay */}
        <div
          className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-[60] transition-opacity duration-300 ${
            isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={onClose}
        />

        <div
          className={`fixed w-full lg:w-[550px] bottom-0 lg:top-0 lg:right-0 h-auto max-h-[85dvh] lg:max-h-none lg:h-full bg-white z-[61] transform transition-transform duration-500 ease-in-out p-4 overflow-y-auto rounded-t-2xl lg:rounded-none ${
            show ? 'translate-y-0 lg:translate-x-0' : 'translate-y-full lg:translate-x-full lg:translate-y-0'
          }`}
        >
          {/* Close Icon */}
          <div className="flex justify-end w-full lg:pb-4">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]"
              onClick={onClose}
            >
              <img src={CloseIcon} alt="CloseIcon" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <h1 className="text-4xl font-semibold lg:mt-3 mb-1 capitalize">{t('welcome')}</h1>

            <p className="text-[#918d8d] text-sm">{t('logintext')}</p>

            <p className="text-base font-semibold mt-4 lg:mt-6 mb-1">{t('log in')}</p>

            <p className="text-[#918d8d] text-sm">{t('welcomeback')}</p>

            <div className="mt-4 lg:mt-6 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] w-full flex items-center justify-between">
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="placeholder:font-light w-full outline-none h-full flex-1 bg-transparent border-none"
                required
              />
              <button
                type="button"
                className="text-sm font-medium text-[#0671e3] uppercase"
                onClick={() => setUserEmail('')} // Reset email for changing if needed
              >
                {t('modify')}
              </button>
            </div>

            <div className="mt-3 lg:mt-5">
              <div className="flex items-center justify-between gap-2 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0">
                <input
                  type={showPassword ? 'text' : 'password'}
                  ref={passwordInputRef}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterpassword')}
                  className="placeholder:font-light w-full outline-none h-full flex-1 bg-transparent border-none"
                  required
                />

                {/* Show/Hide Password Icon */}
                <button
                  type="button"
                  onClick={handlePasswordToggle}
                  className="flex items-center justify-center w-10 h-full"
                >
                  {showPassword ? (
                    <GoEye className="w-6 h-6 text-black opacity-55" />
                  ) : (
                    <GoEyeClosed className="w-6 h-6 text-black opacity-55" />
                  )}
                </button>
              </div>
              <div className="text-right mt-2">
                <button
                  type="button"
                  onClick={() => setIsForgotOpen(true)}
                  className="text-sm text-[#0671e3] font-medium underline-offset-2 hover:underline"
                >
                  {t('forgotpassword')}?
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="h-[52px] px-5 text-center bg-[#0b1223] text-white text-base rounded-lg mt-8 lg:mt-12 w-full font-medium transition-colors duration-300"
              disabled={loading}
            >
              {loading ? 
                <img
                  src={SpinnerIcon}
                  alt={t('loggingin')}
                  className="h-5 w-5 animate-spin mx-auto"
                /> : t('log in')}
            </button>
          </form>
        </div>
        <ForgotPasswordModal
          isOpen={isForgotOpen}
          onClose={() => setIsForgotOpen(false)}
          initialEmail={userEmail}
        />
        <Toast message={toastMessage} type={toastType} />
      </div>
    </>,
    document.body,
  );
};

export default LoginModalHome;
